import {observer} from 'mobx-react-lite'
import React, {useEffect, useState} from 'react'
import {Lang} from 'yougile-lang'
import {logger} from 'yougile-util'
import {isRoleEmail} from 'yougile-model'
import {PermissionsChecker} from '@client/permissions-checker'
import {Button} from '@components/common/base/buttons'
import {Input} from '@components/common/base/input'
import {ErrorBoundary} from '@components/common/error-boundary/error-boundary'
import {UserAvatarV2} from '@components/common/user-avatar-v2/user-avatar-v2'
import {UserName} from '@components/user-name'
import {useForceUpdate, useGlobalKeyDown} from '@containers/hooks'
import {cn} from '@containers/html-utils'
import {globals} from '@extensions/globals-base'
import {UserCardDepartments} from './user-card-departments'
import {UserCardProjects} from './user-card-projects'
import {
  UserCardContext,
  UserCardStore,
  useUserCardStore
} from './user-card-store'

export const UserCard = () => {
  const [userCardStore, setUserCardStore] = useState<UserCardStore>()
  useEffect(() => {
    const userCardStore = new UserCardStore()
    setUserCardStore(userCardStore)
    return userCardStore.dispose
  }, [])

  return (
    <ErrorBoundary
      silent
      onError={e => logger.error('user card error ' + e.message)}
    >
      <UserCardContext.Provider value={userCardStore}>
        {!!userCardStore && <UserCardBase />}
      </UserCardContext.Provider>
    </ErrorBoundary>
  )
}

const UserCardBase = observer(() => {
  const [state] = useUserCardStore()

  if (!state.show || !state.user) {
    return null
  }
  return <UserCardPopup />
})

const UserCardPopup = observer(() => {
  const [state, store] = useUserCardStore()
  const forceUpdate = useForceUpdate()

  useGlobalKeyDown({
    escape: store.close
  })

  const actorIsAdmin = PermissionsChecker.isAdmin()

  const userRole = state.user?.userRole
  const isRole = isRoleEmail(state.user?.userEmail || '')

  return (
    <div className="prj-invite prj-invite--user">
      <div className="prj-invite-overlay" onClick={store.close} />
      <div className="prj-invite-wnd">
        <div
          className={cn('loading', 'transparent', !state.wait && 'hidden')}
        />

        <div className={cn('prj-invite-cnt', state.wait && 'hidden')}>
          <div className="prj-invite-head">
            <span>
              <UserName userId={state.user.userId} />
            </span>
            <div className="prj-invite__close" onClick={store.close} />
          </div>

          <div className="prj-invite-body" onScroll={store.onScroll}>
            <div
              className={cn(
                'prj-invite__card',
                PermissionsChecker.isAdmin({userId: state.user.userId}) &&
                  'admin'
              )}
            >
              <div className="prj-invite__card-ava">
                <UserAvatarV2 {...state.user} size={64} />
              </div>
              <div className="prj-invite__card-info">
                {!isRole && (
                  <div className="prj-invite__card-status">
                    {store.getInfoStatus()}
                  </div>
                )}

                {!isRole && (
                  <div className="prj-invite__position">
                    <i className="fa fa-sitemap" />
                    <span className="prj-invite__position-label">
                      {Lang.msg.companies.position}:
                    </span>
                    {!state.positionEdit && (
                      <span
                        onClick={store.positionStartEdit}
                        className={cn(
                          !store.positionCanEdit() &&
                            'prj-invite__position-cnt--no-edit'
                        )}
                      >
                        {state.positionTitle && (
                          <span className="prj-invite__position-title prj-invite__position-title--editable">
                            {state.positionTitle}
                          </span>
                        )}

                        {!state.positionTitle && (
                          <span className="prj-invite__no-position">
                            {Lang.msg.companies.positionNotSet}
                          </span>
                        )}
                      </span>
                    )}

                    {state.positionEdit && (
                      <Input
                        placeholder={Lang.msg.companies.positionPl}
                        defaultValue={state.positionTitle}
                        onSubmit={store.positionSubmit}
                        onCancel={store.positionCancel}
                        onBlur={store.positionSubmit}
                        onKeyUp={store.positionKeyup}
                        onInit={store.positionInit}
                        autoFocus
                      />
                    )}

                    {state.positionEdit && (
                      <span
                        className="prj-invite__position-btn"
                        onClick={() =>
                          store.positionSubmit({text: state.positionEditStart})
                        }
                      />
                    )}
                  </div>
                )}

                {userRole && (
                  <div className="flex flex-col gap-0 body-long-regular text-secondary ml-24 mt-[-4px]">
                    <div style={{letterSpacing: '-0.2px'}}>
                      {Lang.msg.invitePopup.userCardCaption}
                    </div>
                    <div>{userRole}</div>
                  </div>
                )}

                {store.canSeeEmails() && (
                  <div className="prj-invite__position">
                    <i className="fa fa-at" />
                    <span className="prj-invite__position-label">
                      {Lang.msg.email}:
                    </span>
                    <span className="prj-invite__position-title">
                      {state.user.userEmail}
                    </span>
                  </div>
                )}

                {!isRole && (
                  <div className="prj-invite__card-toggle-admin">
                    {PermissionsChecker.isAdmin({
                      userId: state.user.userId
                    }) && <span>{Lang.msg.permission.isAdmin}.</span>}

                    {actorIsAdmin && (
                      <div
                        className="prj-invite__card-toggle-admin-btn"
                        onClick={() => store.toggleAdmin().then(forceUpdate)}
                      >
                        {PermissionsChecker.isAdmin({userId: state.user.userId})
                          ? Lang.msg.permission.removeAdmin
                          : Lang.msg.permission.addAdmin}
                      </div>
                    )}
                  </div>
                )}

                {actorIsAdmin && (
                  <div className="prj-invite__card-remove">
                    <div
                      className="prj-invite__card-remove-btn"
                      onClick={() => store.deleteUser()}
                    >
                      {Lang.msg.permission.deleteUser}
                    </div>
                  </div>
                )}

                {!isRole && state.user.userId !== globals.currentUserId && (
                  <div
                    className="prj-invite__chat-btn"
                    onClick={() => store.openChat()}
                  >
                    {Lang.msg.openDirectChat}
                  </div>
                )}
              </div>
            </div>

            <UserCardDepartments />
            <UserCardProjects />
          </div>

          <div className={cn('prj-invite-footer', state.wait && 'hidden')}>
            <Button
              onClick={store.submit}
              disabled={state.user && !state.changed}
              className="prj-invite-submit"
            >
              {Lang.msg.saveChanges}
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
})
